/**
 * Factory for creating Algolia product search configurations
 * Eliminates duplication across product-specific config files
 */

import { configure as configureWidget } from 'instantsearch.js/es/widgets';
import { parseJson, buildCompleteFilterString } from './filterUtils.js';

/**
 * Create an Algolia configure widget for a product search grid
 *
 * @param {string} selector - DOM selector for the search container (e.g., '[data-search-robots]')
 * @param {Object} options - Configuration options
 * @param {number} [options.hitsPerPage=24] - Number of results per page
 * @param {boolean} [options.distinct=true] - Enable distinct results
 * @param {boolean} [options.facetingAfterDistinct=true] - Apply faceting after distinct
 * @returns {Object|null} InstantSearch configure widget or null if container not found
 *
 * @example
 * // In robots/config.js:
 * export const configure = createProductConfig('[data-search-robots]')
 *
 * // With custom options:
 * export const configure = createProductConfig('[data-search-robots]', {
 *   hitsPerPage: 12
 * })
 */
export function createProductConfig(selector, options = {}) {
  const wrapper = document.querySelector(selector);

  if (!wrapper) {
    return null;
  }

  const {
    hitsPerPage = 24,
    distinct = true,
    facetingAfterDistinct = true,
  } = options;

  const filtersJson = wrapper.dataset.filter ?? '{}';
  const siteId = wrapper.dataset.siteId;
  const customFilters = parseJson(filtersJson);

  const filterString = buildCompleteFilterString(siteId, customFilters);

  return configureWidget({
    hitsPerPage,
    distinct,
    facetingAfterDistinct,
    filters: filterString,
  });
}
